import { faBookmark } from '@fortawesome/pro-regular-svg-icons/faBookmark';
import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons/faThumbsUp';
import { faCircleExclamationCheck } from '@fortawesome/pro-solid-svg-icons/faCircleExclamationCheck';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { alpha, Avatar, Box, CardActionArea, CardMedia, Theme } from '@mui/material';
import { AppImageDimension } from 'app/appImages';
import { MprcText } from "components";
import { LmsCardVariants, MprcCardTypography } from 'components/MprcCardTypography';
import { CardAction, CardActionType, CardColor, CardDto, CardIcon, CardIconVariant, CardSticker, CardText, CardTextVariant } from "generated/CoreApi_v1";
import { useLocation, useNavigate } from 'react-router';
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { LmsPaths } from 'routes/LmsRouter';
import { RootPaths } from 'routes/RootRouter';
import { FeedShortInitialFilter } from 'views/knowledge/feed/FeedShortVideoExplorer';

///////////////////////////////////////////////////////////////////////////////
export interface ICardDtoPathResult {
    url: string,
    state?: any;
}
interface ICardDtoAction {
    onCarouselReload?: () => void;
    onFullReload?: () => void;
    onClearCache?: () => void;
    saveState?: any;
}

export interface ICardDtoActionsProps extends ICardDtoAction {
    item: CardDto;
}

export interface ICardDtoToCardRibbonOptions extends ICardDtoAction {
}

export interface IBuildCardProps extends ICardDtoActionsProps, ICardDtoToCardRibbonOptions {
}

///////////////////////////////////////////////////////////////////////////////

export function discoverPath(action: CardAction, data: Record<string, any>): ICardDtoPathResult | undefined {
    switch (action.actionType) {
        case CardActionType.LmsOpenAssessment:
            return { url: LmsPaths.BuildEnrollmentViewer(data.trainingType!, data["enrollmentId"]!) + "?oos" };
        case CardActionType.LmsEnrollmentView:
            return { url: LmsPaths.BuildEnrollmentViewer(data.trainingType!, data["enrollmentId"]!, data["meetingComponentId"]) };
        case CardActionType.PublicationView:
        case CardActionType.PublicationPreview:
            return { url: LmsPaths.PublicationViewerResolver(data["publicationId"]!, data["publicationType"]!) };
        case CardActionType.LmsLibraryView:
            return { url: LmsPaths.StudentLibrary };
        case CardActionType.TopicPublicationsView:
            return { url: KnowledgePaths.FeedTopic(data["topicId"]!) };
        case CardActionType.TopicTrainingsView:
            return { url: KnowledgePaths.FeedTopicTrainings(data["topicId"]) };
        case CardActionType.ExternalLink:
            return { url: data["externalLink"] };
        case CardActionType.LmsPreApproval:
            return { url: LmsPaths.Approvals, state: { startTab: "PreApprovals" } };
        case CardActionType.LmsConclusionFormApproval:
            return { url: LmsPaths.Approvals, state: { startTab: "ConclusionForm" } };
        case CardActionType.LmsTutoring:
            return { url: LmsPaths.Supervisor, state: { startTab: "Instructor" } };
        case CardActionType.LmsCoordinating:
            return { url: LmsPaths.Supervisor, state: { startTab: "Coordinator" } };
        case CardActionType.KnowledgeCustomList:
            return { url: data["openInPlayback"] == true ? `${KnowledgePaths.ListPublicationInPlayback}/${data["knowledgeListId"]}` : KnowledgePaths.FeedList(data["knowledgeListId"]) };
        case CardActionType.KnowledgeFavorites:
            return { url: KnowledgePaths.FeedFavorites };
        case CardActionType.KnowledgeSeeLater:
            return { url: KnowledgePaths.FeedSeeLater };
        case CardActionType.KnowledgeModeration:
            return { url: KnowledgePaths.FeedModeration };
        case CardActionType.KnowledgeShorts:
            return { url: KnowledgePaths.FeedShorts, state: { publication: { publicationId: data["publicationId"]!, mediaFileId: data["mediaFileId"] } } as FeedShortInitialFilter };
        case CardActionType.UserProfile:
            return { url: RootPaths.BuildUserProfile(data["userId"]) };
    }
    return undefined;
}

///////////////////////////////////////////////////////////////////////////////

export function buildColor(color: CardColor): MprcColors {
    switch (color) {
        case CardColor.Error:
            return "error";
        case CardColor.Success:
            return "success";
        case CardColor.Muted:
            return "muted";
        case CardColor.Warning:
            return "warning";
    }
    return "muted";
}

///////////////////////////////////////////////////////////////////////////////

export function buildStickerColor(color: string, theme: Theme): string {

    if (color.startsWith("#")) return alpha(color, 0.8);

    switch (CardColor[color as keyof typeof CardColor]) {
        case CardColor.Error:
            return theme.palette.error.main;
        case CardColor.Success:
            return theme.palette.success.main;
        case CardColor.Muted:
            return theme.palette.muted.main;
        case CardColor.Warning:
            return theme.palette.warning.main;
    }

    return color;
}

///////////////////////////////////////////////////////////////////////////////

export const RenderCardIcon = ({ icon, variant}: { icon: CardIcon | null; variant?: "pre" | "post", color?: MprcColors | [MprcColors, MprcColorVariant] | "inherit"; }) => {
    if (icon == null) return null;

    const style = variant == undefined
        ? undefined
        : variant == "pre" ? { marginRight: 5 } : { marginLeft: 5 };

    const renderIcon = () => {
        switch (icon.variant) {
            case CardIconVariant.Bookmark:
                return <FontAwesomeIcon style={style} icon={faBookmark} />;
            case CardIconVariant.ThumbsUp:
                return <FontAwesomeIcon style={style} icon={faThumbsUp} />;
            case CardIconVariant.CircleExclamationCheck:
                return <FontAwesomeIcon style={style} icon={faCircleExclamationCheck} />;
            case CardIconVariant.Lock:
                return <FontAwesomeIcon style={style} icon={faLock} />;
        }
    };

    return (
        <MprcText color={icon.color != null ? CardColor[icon.color].toCamelcase() as MprcColors : "inherit"} title={icon.tooltip ?? ""}>
            {renderIcon()}
        </MprcText>
    );
};

///////////////////////////////////////////////////////////////////////////////

export const RenderCardText = ({ line }: { line: CardText | null; }) => {
    if (line == null) return line;

    return (
        <MprcCardTypography
            noWrap
            variant={CardTextVariant[line.variant].toCamelcase() as LmsCardVariants}
            color={line.color != null ? CardColor[line.color].toCamelcase() as MprcColorsExtended : undefined}
            reverse={line.reverse}
            tooltip={line.text}
        >
            <RenderCardIcon icon={line.preIcon} variant='pre' />
            {line.text}
            <RenderCardIcon icon={line.postIcon} variant='post' />
        </MprcCardTypography>
    );
};

///////////////////////////////////////////////////////////////////////////////

export const RenderCardSticker = ({ item }: { item: CardSticker | null; }) => {
    return item == null
        ? null
        : (
            <Box
                sx={
                    (theme) => (
                        {
                            ...theme.extended.lmsCard.sticker as any,
                            color: buildStickerColor(item.foregroundColor, theme),
                            backgroundColor: item.backgroundColor,
                        }
                    )
                }
            >
                <strong>{item.text}</strong>
            </Box>
        );
};

///////////////////////////////////////////////////////////////////////////////

export const CardThumbnail = ({ thumbnailUrl, thumbnailLink, saveState, onClick }: { thumbnailUrl: string; thumbnailLink?: string; saveState?: any; onClick?: () => void; }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const RenderContent = (
        <CardMedia
            component="img"
            width={AppImageDimension.ThumbnailWide.width}
            height={AppImageDimension.ThumbnailWide.height}
            image={thumbnailUrl}
            sx={{ objectFit: "contain" }}
        />
    );

    return thumbnailLink
        ? <CardActionArea
            onClick={() => {
                if (saveState) {
                    navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), saveState } });
                    navigate(thumbnailLink);
                } else {
                    navigate(thumbnailLink);
                }
            }}
        >
            {RenderContent}
        </CardActionArea>
        : onClick ?
            <CardActionArea onClick={onClick}>
                {RenderContent}
            </CardActionArea>
            : RenderContent;
};

///////////////////////////////////////////////////////////////////////////////

export const CardThumbnailAvatar = ({ thumbnailUrl, thumbnailLink, saveState }: { thumbnailUrl: string; thumbnailLink?: string; saveState?: any; }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const RenderContent = (
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
            <Avatar
                sx={
                    theme => ({
                        width: AppImageDimension.ThumbnailWide.height - parseInt(theme.spacing(1)),
                        height: AppImageDimension.ThumbnailWide.height - parseInt(theme.spacing(1))
                    })
                }
                src={thumbnailUrl}
            />
        </Box>
    );

    return thumbnailLink
        ? <CardActionArea
            onClick={() => {
                if (saveState) {
                    navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), saveState } });
                    navigate(thumbnailLink);
                } else {
                    navigate(thumbnailLink);
                }
            }}
        >
            {RenderContent}
        </CardActionArea>
        : RenderContent;
};

///////////////////////////////////////////////////////////////////////////////
